import { canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import type { FieldKey, ImportPlan } from '@core/units/stockImport';

/**
 * The import screens' small rules that are the website's own rather than the core's — what the
 * button says, what a role may not do, how a photo file is found, where a layout is kept.
 */

/** Why the import cannot go ahead as planned, or null — a write the server would refuse is never offered. */
export function importBlocker(session: Session, plan: ImportPlan): string | null {
  if (plan.summary.garments === 0) {
    return 'No row is ready to import.';
  }

  if (plan.newDesigns.length > 0 && !canAct(session, 'Item:Create')) {
    return `${plan.newDesigns.length} new design${plan.newDesigns.length === 1 ? '' : 's'} would be created, and your role cannot create designs. Leave those rows out, or ask someone who can.`;
  }

  if (plan.newValues.length > 0 && !canAct(session, 'ItemAttribute:Edit')) {
    return 'New sizes or colours would be added, and your role cannot add them.';
  }

  return null;
}

export function importLabel(plan: ImportPlan): string {
  const rows = plan.summary.ready + plan.summary.warning;

  return `Import ${rows} row${rows === 1 ? '' : 's'} · ${plan.summary.garments} garment${plan.summary.garments === 1 ? '' : 's'}`;
}

/** A value's code from the sheet's word: "Off white" → OFF_WHITE, as the Attributes page expects. */
export function valueCode(word: string): string {
  return (
    word
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'VALUE'
  );
}

/** A photo file's name as the photo column would write it: case and folders ignored. */
export function photoKey(name: string): string {
  return (name.split(/[\\/]/).pop() ?? name).trim().toLowerCase();
}

/** The name without its extension, for a column that writes "zari-01" for "zari-01.jpg". */
export function stem(name: string): string {
  return photoKey(name).replace(/\.[a-z0-9]+$/, '');
}

/* ----------------------------------------------------------------- layouts */

/** A column layout kept for the next sheet with the same headings — this browser only. */
export interface SavedLayout {
  name: string;
  mapping: FieldKey[];
  usedOn: string;
}

const layoutStore = 'attirerent.stockImport.layouts';

export function readLayouts(): Record<string, SavedLayout> {
  try {
    return JSON.parse(window.localStorage.getItem(layoutStore) ?? '{}') as Record<
      string,
      SavedLayout
    >;
  } catch {
    return {};
  }
}

export function keepLayout(key: string, layout: SavedLayout) {
  try {
    window.localStorage.setItem(layoutStore, JSON.stringify({ ...readLayouts(), [key]: layout }));
  } catch {
    // Storage refused (a private window): the next sheet is matched by its headings instead.
  }
}
